// Copy for /services/jewelry-design/ and /services/manufacturing/
// (ProductServicePage, code pages since 2026-11-04). Both use the published
// 3D printing facts (./printing-service: printing from about $30, modeling
// and scanning from $250, no setup fee for 3D printing, an in-house FDM print
// farm, next-day printing for many jobs, NYC courier, all 50 states) plus
// their own truth.md rows:
// - Jewelry: 3D modeling, resin prototypes and metal-plated finishing for
//   reviewed concepts. Casting, precious-metal production, stone setting and
//   certification are not implied; CAD timing is per project. Plating adds a
//   metal surface, it doesn't make the piece solid metal.
// - Manufacturing: prototype to production for businesses. Production runs
//   in-house on the print farm, resin and metal printers and CNC; methods we
//   don't run (injection molding and the like) are coordinated with an
//   approved manufacturing partner when applicable, with no promise that
//   every prototype can go to mass production. No regulated-industry or
//   certification claims.
// Anything else is quoted per project. Quotes open the 3D printing planner.
const printing = require('./printing-service')

const { pricing } = printing
const photoRoot = 'https://images.ctfassets.net/dovsnf6qrcem'

const jewelryPhotos = {
  rings: {
    url: `${photoRoot}/5sZQ5SbxoZCIPgnVmHGw9v/38a25378def5582684a6f328fec5b1f7/metlab-ring_fj2lmf.jpg`,
    width: 1600,
    height: 1068,
    alt: 'Three 3D printed two-finger rings spelling a brand name, one red and two with a metal-plated finish',
    title: 'Brand-name rings',
    caption: 'Printed in resin, then left red or metal plated.',
  },
  snakes: {
    url: `${photoRoot}/7v7MIzjE0i4CtUB1aOzCnE/d7f45e539941ae2937f9d77a8cff8c46/snake_xb35bl.jpg`,
    width: 1600,
    height: 1149,
    alt: 'Two intertwined snake jewelry pieces with scale texture, metal plated',
    title: 'Plated snake pieces',
    caption: 'Fine scale texture modeled in 3D and metal plated.',
  },
  hands: {
    url: `${photoRoot}/1FtshuNxd2xin3rbga5Sks/7aeec919a01ab3c799ebcba7469e398b/WechatIMG21226.jpeg`,
    width: 3000,
    height: 2002,
    alt: 'A 3D printed praying-hands sculpture plated in gold and blue chrome',
    title: 'Gold and chrome plating',
    caption: 'The plated finishes we use on jewelry-scale pieces.',
  },
  chrome: printing.photos.chrome,
  busts: {
    url: `${photoRoot}/2FFjdkhKKVmhWz5QXbLrwU/caeb8912a06c04145f2df6944f8fb442/LAL_4330.jpg`,
    width: 7360,
    height: 4912,
    alt: 'A grey resin 3D printed dragon and warrior bust on a black background',
    title: 'Fine-detail resin',
    caption: 'The resin detail a jewelry prototype is printed in.',
  },
}

const jewelry = {
  page: {
    path: '/services/jewelry-design/',
    title: 'Custom Jewelry Design, CAD & 3D Printed Prototypes | NYC',
    description: `Custom jewelry design in New York: jewelry CAD and 3D modeling from $${pricing.modelingFrom}, 3D printed resin prototypes and metal-plated pieces for brands.`,
    heading: 'Custom Jewelry Design and Prototyping',
    eyebrow: 'Jewelry CAD · 3D modeling · 3D printed prototypes',
    crumb: 'Custom jewelry design',
    answer: `PrintAWorld turns jewelry sketches, photos and references into 3D models and 3D printed prototypes in our New York studio. We model the piece in CAD (3D modeling starts at $${pricing.modelingFrom}), print it in fine-detail resin so you can check the size, fit and look, and can finish it with metal plating for samples, shoots and statement pieces. Have a file already? Printing starts around $${pricing.printFrom}. We don’t cast gold or set stones; your approved model is ready to take to a caster.`,
    hero: 'rings',
    reviewedDate: '2026-11-04',
  },
  photos: jewelryPhotos,
  intro: {
    eyebrow: '3D printed jewelry',
    heading: 'What does a jewelry design and prototyping studio do?',
    paragraphs: [
      'We sit between your sketch and the caster. You bring the idea; we build it in 3D, print it so you can hold it, and change it until the proportions are right. Then you have a model you’ve tested, not a drawing you hope works.',
      'Our clients are designers, brands and artists: a ring or pendant to approve before casting, a sample collection for a pitch or a photoshoot, oversized or costume pieces finished in metal plating, and jewelry for props and displays.',
    ],
  },
  glanceHeading: 'Our custom jewelry design service at a glance',
  glance: [
    [
      'What we do',
      'Jewelry CAD and 3D modeling, resin 3D printed prototypes, metal-plated finishing',
    ],
    [
      'Who it’s for',
      'Jewelry designers, brands, artists and agencies; samples, launches and props',
    ],
    ['Starting point', 'A sketch, photos, a reference piece or a 3D file'],
    ['3D modeling', `From $${pricing.modelingFrom}`],
    [
      'Printing',
      `From about $${pricing.printFrom} for a print from a ready file`,
    ],
    [
      'Finishes',
      'Raw resin, sanded and painted, or metal plated (gold, chrome or bronze looks)',
    ],
    ['Minimum', 'One piece. No setup fee for 3D printing.'],
    [
      'Not included',
      'Precious-metal casting, stone setting and certification, unless your quote lists them',
    ],
    ['Turnaround', 'Quoted per project; tell us your date'],
    [
      'Where',
      'Our New York studios in Queens and Brooklyn; ships to all 50 states',
    ],
  ],
  workHeading: 'Jewelry and fine-detail pieces from our studio',
  work: ['snakes', 'hands', 'busts', 'chrome'],
  offersEyebrow: 'Jewelry services',
  offersHeading: 'Which jewelry design services do we offer?',
  offers: [
    {
      label: '01',
      title: 'Jewelry CAD and 3D modeling',
      body: `We model rings, pendants, earrings and charms from your sketch or photos, and send renders to approve. From $${pricing.modelingFrom}.`,
    },
    {
      label: '02',
      title: '3D printed jewelry prototypes',
      body: 'Fine-detail resin prints to check size, fit and proportions on the hand before you cast, and to show buyers.',
    },
    {
      label: '03',
      title: 'Metal-plated pieces',
      body: 'A gold, chrome or bronze surface over a 3D print, for samples, photoshoots, costume and statement pieces. Plating doesn’t make it solid metal.',
    },
    {
      label: '04',
      title: 'Sample runs for brands',
      body: 'A set of matching pieces for a launch, a pitch or an event, printed and finished together.',
    },
  ],
  offersLinks: [
    ['3D modeling', '/services/3d-modeling/'],
    ['Metal plating', '/services/metal-plating/'],
    ['SLA resin 3D printing', '/services/sla-3d-printing/'],
  ],
  contrast: {
    eyebrow: 'Before you cast',
    heading: 'Why make a 3D printed prototype of your jewelry?',
    paragraphs: [
      'A screen hides scale. A printed prototype shows whether the band is too thick, the stone seat too high or the pendant too heavy-looking, and costs far less than recasting metal.',
      'Once you’ve approved it, take the model to your caster or jeweler for metal, casting and stones. We don’t cast precious metal or set stones ourselves.',
    ],
  },
  processHeading: 'How custom jewelry design works with us',
  steps: [
    {
      title: 'Send the idea',
      body: 'A sketch, photos, a piece to copy or a 3D file, with the size (ring size or dimensions) and how many.',
    },
    {
      title: 'We model it',
      body: 'We build the piece in 3D and send renders. You ask for changes before anything is printed.',
    },
    {
      title: 'Print and check',
      body: 'We print it in fine-detail resin so you can try it on, photograph it or show it.',
    },
    {
      title: 'Finish or hand off',
      body: 'Plate or paint it as a finished piece, or take the approved model to your caster.',
    },
  ],
  optionsEyebrow: 'Your options',
  optionsHeading: 'Where can you get a custom jewelry design made?',
  options: [
    {
      label: 'O1',
      title: 'A jeweler',
      body: 'Designs, casts in gold or platinum, sets stones and sells the finished piece. Best for an engagement ring or fine jewelry you’ll wear every day.',
    },
    {
      label: 'O2',
      title: 'A casting house or CAD service',
      body: 'Makes casting-ready files and waxes and casts them in metal. Best when the design is settled and you need it in precious metal.',
    },
    {
      label: 'O3',
      title: 'A design and fabrication studio, like ours',
      body: 'Models the piece, prints prototypes you can hold and finishes them with plating. Best for designers and brands still shaping a design, samples and statement pieces.',
    },
  ],
  fit: {
    good: [
      'Designers who want to try a piece on before casting it',
      'Brands making jewelry samples for a launch, pitch or photoshoot',
      'Statement, costume and oversized pieces finished in metal plating',
      'Jewelry for props, displays and film',
    ],
    other: [
      'Solid gold or platinum pieces: a caster or jeweler',
      'Stone setting and certified fine jewelry: a jeweler',
      'Repairs and resizing of jewelry you own: a jeweler',
    ],
  },
  pricing: {
    heading: 'How much does custom jewelry design cost?',
    leadLabel: 'Jewelry 3D modeling starts at',
    leadAmount: `$${pricing.modelingFrom}`,
    leadNote: 'To turn a sketch or photos into a 3D model you approve.',
    rows: [
      ['3D modeling (CAD)', `From $${pricing.modelingFrom}`],
      ['3D print from a ready file', `From about $${pricing.printFrom}`],
      ['Setup fee for 3D printing', 'None'],
      ['Metal plating', 'Quoted per project'],
      ['Sample runs', 'Quoted per piece'],
    ],
    note: 'For planning only. Detail, size, revisions, quantity and finish set the final price, confirmed in your written quote.',
  },
  faqHeading: 'What do people ask about custom jewelry design?',
  faqs: [
    {
      question: 'How much does custom jewelry design cost?',
      answer: `Jewelry 3D modeling starts at $${pricing.modelingFrom}, and a 3D print from a ready file starts around $${pricing.printFrom}. The final price depends on the detail, size, revisions, quantity and finish, and your written quote confirms it before we start. Metal plating is quoted per project.`,
    },
    {
      question: 'Can you turn my jewelry sketch into a 3D model?',
      answer:
        'Yes. Send the sketch with the ring size or dimensions and any photos of pieces you like. We model it in 3D, send renders and change it with you before anything is printed.',
    },
    {
      question: 'Do you make 3D printed jewelry prototypes?',
      answer:
        'Yes. We print jewelry in fine-detail resin so you can check the size, fit and proportions, try it on and photograph it before you commit to metal.',
    },
    {
      question: 'Do you cast jewelry in gold or silver?',
      answer:
        'No. We don’t cast precious metal or set stones. We can metal-plate a 3D print for a gold, chrome or bronze look, or you can take the approved model to your caster or jeweler.',
    },
    {
      question: 'Is metal-plated jewelry solid metal?',
      answer:
        'No. Plating adds a metal surface over the 3D print. It suits samples, photoshoots, costume and statement pieces; for everyday fine jewelry, cast metal is the better choice.',
    },
    {
      question: 'Will I get the 3D file?',
      answer:
        'The file format and what you receive are listed in your written quote. Ask for the file you need, for example an STL for your caster, when you request the quote.',
    },
    {
      question: 'How long does jewelry CAD take?',
      answer:
        'It depends on the design, the detail and the number of revisions. Your written quote confirms the schedule before modeling starts; tell us your date up front.',
    },
    {
      question: 'Can you make a small run of jewelry samples for a brand?',
      answer:
        'Yes. We print and finish matching pieces for launches, pitches and events, from one sample to a set. Each run is quoted per piece.',
    },
  ],
  checklistHeading: 'What to send',
  checklist: [
    'A sketch, photos or a reference piece',
    'Ring size or dimensions',
    'The finish: raw resin, painted or metal plated',
    'How many pieces',
    'Your date and delivery ZIP',
  ],
  quote: {
    eyebrow: 'Let’s make your piece',
    heading: 'Send your sketch. We’ll send a price.',
    body: 'Send the sketch, photos or file and tell us what it’s for. We’ll reply with a price for the model and the prototype.',
    button: 'Get a jewelry design quote',
  },
  schema: {
    name: 'Custom Jewelry Design and Prototyping',
    serviceType:
      'Jewelry CAD, 3D modeling, resin 3D printed prototypes and metal-plated finishing',
    minPrice: pricing.modelingFrom,
    offer: `Jewelry 3D modeling starts at $${pricing.modelingFrom}; 3D prints from a ready file start around $${pricing.printFrom}; plating and runs are quoted per project.`,
  },
}

const manufacturingPhotos = {
  enclosure: {
    url: `${photoRoot}/3FOhVrnDkIGKo2SQOgeIgA/a2484241e645bdbb9429797e34606c6c/LAL_5651.jpg`,
    width: 3000,
    height: 2002,
    alt: 'The back of a black 3D printed electronics enclosure with port cutouts, vents and screw holes',
    title: 'Electronics enclosure',
    caption: 'A device enclosure with ports, vents and mounting holes.',
  },
  hingedCase: {
    url: `${photoRoot}/7Cwab2mSCOD71ge85b6fi6/94b19fec706f72bc2d2c6c4dd4c94c44/LAL_5865.jpg`,
    width: 3000,
    height: 2002,
    alt: 'An open black and grey 3D printed hinged case with a divided tray',
    title: 'Hinged case',
    caption: 'A hinged case printed to test the lid and the inner tray.',
  },
  housing: {
    url: `${photoRoot}/1psOt2F2Ve07763YFaaaYC/9e40dc77fb2c1582fd71411fc25db83e/LAL_5873.jpg`,
    width: 3000,
    height: 2002,
    alt: 'A three-part 3D printed product housing laid open to show the internal clips and parts',
    title: 'Multi-part housing',
    caption: 'Housing parts printed to check how they clip together.',
  },
  keychains: {
    url: `${photoRoot}/k91uQXnoGhtqVfx2JNV7i/cc99217f15a635c270b9d6140f8efd24/LAL_5878.jpg`,
    width: 3000,
    height: 2002,
    alt: 'Two red and black sneaker-shaped keychains on metal clasps',
    title: 'Branded keychains',
    caption: 'A small branded product, printed and painted as a run.',
  },
  prototype: printing.photos.prototype,
  dispenser: {
    ...printing.photos.dispenser,
    title: 'Dispenser prototype',
    caption: 'An open-front prototype to test the mechanism.',
  },
  gear: {
    ...printing.photos.gear,
    title: 'Stainless steel part',
    caption: 'A stainless-steel impeller printed by SLM.',
  },
}

const manufacturing = {
  page: {
    path: '/services/manufacturing/',
    title: 'Manufacturing Service: Prototype to Small-Batch Production',
    description: `Prototype to small-batch manufacturing in New York: 3D modeling, prototypes and production runs on our in-house print farm, resin, metal and CNC.`,
    heading: 'Manufacturing Service: Prototype to Production',
    eyebrow: 'Product development · small-batch manufacturing',
    crumb: 'Manufacturing',
    answer: `PrintAWorld takes products from prototype to production for businesses, brands and product teams. We model the part if you only have a sketch (3D modeling from $${pricing.modelingFrom}), print prototypes until it’s right, then make the production run in-house in New York on our FDM print farm, resin and stainless-steel metal printers and CNC router, with sanding, painting and plating. There’s no setup fee for 3D printing, many jobs print next day, and we ship to all 50 states. For methods we don’t run, like injection molding, we can bring in a manufacturing partner.`,
    hero: 'enclosure',
    reviewedDate: '2026-11-04',
  },
  photos: manufacturingPhotos,
  intro: {
    eyebrow: 'Product development and manufacturing',
    heading: 'What is a prototype-to-production manufacturing service?',
    paragraphs: [
      'One team from the first sketch to the boxed run. We model the product, print prototypes you can test and hand to customers, change it, and then produce it, so nothing gets lost passing files between a designer, a prototype shop and a factory.',
      'We make small batches and large production orders: product launches, branded merchandise, display and retail parts, and the bridge run before you commit to tooling.',
    ],
  },
  glanceHeading: 'Our manufacturing service at a glance',
  glance: [
    [
      'Who it’s for',
      'Businesses, brands, agencies, startups and product teams',
    ],
    ['Starting point', 'A sketch, photos, a description, a 3D file or CAD'],
    [
      'In-house',
      '3D modeling, an FDM print farm, SLA resin, stainless-steel metal (SLM) printing, CNC routing, sanding, painting and plating',
    ],
    ['Order size', 'One prototype to large production runs'],
    ['Setup fee', 'None for 3D printing'],
    [
      'Price',
      `3D printing from about $${pricing.printFrom}; 3D modeling from $${pricing.modelingFrom}; runs quoted per piece`,
    ],
    [
      'Turnaround',
      'Next-day printing for many jobs; the production schedule is in your quote',
    ],
    [
      'Beyond 3D printing',
      'Injection molding and other methods through an approved manufacturing partner, when it suits the part',
    ],
    [
      'Delivery',
      'Pickup in Queens or Brooklyn, NYC courier, or shipping to all 50 states (next-day shipping available)',
    ],
    [
      'Not offered',
      'Regulated aerospace, medical-device or defense production, and certified parts',
    ],
  ],
  workHeading: 'Prototypes and production parts from our studio',
  work: [
    'housing',
    'hingedCase',
    'keychains',
    'dispenser',
    'prototype',
    'gear',
  ],
  offersEyebrow: 'From idea to run',
  offersHeading: 'Which manufacturing services do we offer?',
  offers: [
    {
      label: '01',
      title: 'Product design and 3D modeling',
      body: `From a sketch, photos or measurements to a model ready to print or machine. From $${pricing.modelingFrom}.`,
    },
    {
      label: '02',
      title: 'Rapid prototyping',
      body: 'FDM, resin and metal prototypes to test fit, function and looks, often next day, so you can change the design quickly.',
    },
    {
      label: '03',
      title: 'Small-batch production',
      body: 'Production runs on our in-house FDM print farm, plus resin, stainless steel and CNC, finished and packed for delivery.',
    },
    {
      label: '04',
      title: 'Finishing and assembly',
      body: 'Sanding, priming, painting in brand colors and metal plating, so parts arrive ready to sell, show or use.',
    },
  ],
  offersLinks: [
    ['3D printing service', '/services/3d-printing/'],
    ['3D modeling', '/services/3d-modeling/'],
    ['Metal 3D printing', '/services/metal-3d-printing/'],
    ['CNC routing', '/services/cnc-routing/'],
  ],
  contrast: {
    eyebrow: 'Before you buy a mold',
    heading: 'Is 3D printing good for small-batch manufacturing?',
    paragraphs: [
      'Often, yes. With no mold to pay for and no setup fee, a few hundred or a few thousand parts can cost less to print than to tool up for, and you can change the design between batches.',
      'When the quantity is high enough that a mold pays for itself, we’ll say so, and we can coordinate the move to injection molding with a manufacturing partner. Not every prototype is ready for mass production, and we’ll tell you what needs changing first.',
    ],
  },
  processHeading: 'How does a product go from prototype to production?',
  steps: [
    {
      title: 'Send what you have',
      body: 'A sketch, photos, CAD or a part to copy, with the quantity, the material if you know it, and your date.',
    },
    {
      title: 'Model and prototype',
      body: 'We model the part and print prototypes. You test them and we change the design until it works.',
    },
    {
      title: 'Agree the production route',
      body: 'Print farm, resin, metal, CNC or a molding partner: your written quote names the method, price per piece and schedule.',
    },
    {
      title: 'Produce and deliver',
      body: 'We make, finish and pack the run, then you pick it up, get it by courier in NYC or we ship it.',
    },
  ],
  optionsEyebrow: 'Your options',
  optionsHeading: 'Where can you get a product manufactured in small batches?',
  options: [
    {
      label: 'O1',
      title: 'An instant-quote manufacturing website',
      body: 'Upload a finished CAD file and get an automatic price from a network of shops. Good for standard engineering parts when the design is settled.',
    },
    {
      label: 'O2',
      title: 'An overseas factory',
      body: 'The lowest unit price at very high volumes, after you pay for molds and wait for samples and freight.',
    },
    {
      label: 'O3',
      title: 'A New York production studio, like ours',
      body: 'One team that models, prototypes and produces in-house, with finishing and fast changes. Best for launches, branded products and runs from a few parts to large production orders.',
    },
  ],
  fit: {
    good: [
      'Product teams moving from prototype to a first production run',
      'Brands and agencies making branded products, merch and display parts',
      'Bridge production before you invest in a mold',
      'Parts that need finishing: painted, plated or assembled',
      'Large production orders on our FDM print farm',
    ],
    other: [
      'Hobby projects or parts you plan to resell cheaply: a marketplace usually costs less',
      'Regulated aerospace, medical-device or defense parts',
      'Very high volumes where an overseas mold is already paid for',
    ],
  },
  pricing: {
    heading: 'How much does small-batch manufacturing cost?',
    leadLabel: '3D printed parts start around',
    leadAmount: `$${pricing.printFrom}`,
    leadNote: 'For a simple part from a file that’s ready to print.',
    rows: [
      ['3D modeling and product design', `From $${pricing.modelingFrom}`],
      ['3D printed prototype from a file', `From about $${pricing.printFrom}`],
      ['Setup fee for 3D printing', 'None'],
      ['Production runs', 'Quoted per piece'],
      ['Finishing, packing, molds', 'Quoted per project'],
    ],
    note: 'For planning only. Size, material, machine time, quantity, finishing and your date set the final price, confirmed in your written quote.',
  },
  faqHeading: 'What do people ask about our manufacturing service?',
  faqs: [
    {
      question: 'Do you do small-batch manufacturing?',
      answer:
        'Yes, from a few parts to large production orders, made in-house in New York on our FDM print farm and our resin, stainless-steel and CNC machines, then finished and packed.',
    },
    {
      question: 'Can you take my product from prototype to production?',
      answer:
        'Yes. We model the product, print prototypes for you to test, change the design, then make the production run. If the part needs a method we don’t run, such as injection molding, we can coordinate it with a manufacturing partner. Your quote names the route.',
    },
    {
      question: 'Is there a minimum order or setup fee?',
      answer: `There’s no setup fee for 3D printing and no minimum: one prototype is fine, and so is a large production order. Printed parts start around $${pricing.printFrom}; runs are quoted per piece.`,
    },
    {
      question: 'How fast can you make prototypes and production parts?',
      answer:
        'Many jobs can print next day, and in New York we can deliver by courier. Production runs are scheduled in your quote based on the quantity, material and finishing. Same-day isn’t standard.',
    },
    {
      question: 'Do you need a CAD file to start?',
      answer: `No. A sketch, photos or a description is enough: 3D modeling starts at $${pricing.modelingFrom}. To copy an existing part, we can 3D scan it first.`,
    },
    {
      question: 'Do you do injection molding?',
      answer:
        'Not in-house. When the quantity makes a mold worth it, we can coordinate injection molding with a manufacturing partner. Not every prototype is ready for molding, and we’ll tell you what needs to change.',
    },
    {
      question: 'Can you paint, plate or assemble the parts?',
      answer:
        'Yes. We sand, prime, paint in your colors and metal plate parts, and can assemble and pack them when it’s in your quote.',
    },
    {
      question: 'Who is the manufacturing service for?',
      answer:
        'Businesses, brands, agencies and product teams. For a hobby project or parts you plan to resell cheaply, a marketplace or hobbyist will usually cost less, and we’re happy to point you there.',
    },
  ],
  checklistHeading: 'What to send',
  checklist: [
    'CAD, a 3D file, a sketch or photos',
    'Quantity, now and later',
    'Material and finish, if you know them',
    'What the part does and where it’s used',
    'Your date and delivery ZIP',
  ],
  quote: {
    eyebrow: 'Let’s make your product',
    heading: 'Send your design. We’ll send a price.',
    body: 'Send the file, sketch or photos and the quantities you’d like priced. We’ll reply with a price for the prototype and the run.',
    button: 'Get a manufacturing quote',
  },
  schema: {
    name: 'Manufacturing Service: Prototype to Production',
    serviceType:
      'Product development, rapid prototyping and small-batch manufacturing',
    minPrice: pricing.printFrom,
    offer: `3D printed parts start around $${pricing.printFrom}; 3D modeling from $${pricing.modelingFrom}; production runs are quoted per piece.`,
  },
}

module.exports = { pricing, jewelry, manufacturing }
